import {w3cwebsocket as W3CWebsocket} from 'websocket';
import {Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {GenginePlayer} from './gengine-player';
import {GengineSettings} from './gengine-settings';
import {Injectable} from '@angular/core';
import {IGengineClient} from './i-gengine-client';
import {IResponse} from './i-response';
import {environment} from '../../../environments/environment';
import {IMessage} from './i-message';
import {TWithPlayers} from './gengine-data-holder';

@Injectable({providedIn: 'root'})
export class GengineClient<TRoomData extends TWithPlayers, TWorldData extends TWithPlayers>
  implements IGengineClient
{
  private client: W3CWebsocket;
  private waiting = false;

  status = new Subject<string>();
  response = new Subject<IResponse>();
  message = new Subject<IMessage>();

  settings = new GengineSettings();

  player?: GenginePlayer<TRoomData, TWorldData>;
  disconnected?: Promise<void>;
  private onDisconnect: () => void;

  constructor() {
  }

  log(...args: any[]): void {
    console.log(new Date().toLocaleTimeString() + ' gengine client', ...args);
  }
  get connected(): boolean {
    return !!this.client;
  }

  async close(): Promise<void> {
    this.client.close();
    this.onDisconnect();
    this.client = null;
    this.waiting = false;
  }

  async connect(): Promise<void> {
    this.log('start opening');
    const openingCBs: any = {};
    const opening = new Promise((resolve, reject) => Object.assign(openingCBs, {resolve, reject}));
    this.client = new W3CWebsocket(this.settings.serverUrl, 'gEngine-protocol'.toLowerCase());
    this.disconnected = new Promise(resolve => {
      return this.onDisconnect = resolve;
    });
    this.client.onerror = () => {
      openingCBs.reject(new Error(`connect error`));
    };
    this.client.onopen = () => {
      this.log(`connection opened`);
      this.client.onmessage = this.handleMessage.bind(this);
      openingCBs.resolve();
      this.status.next('connected');
    };
    this.client.onclose = () => {
      this.onDisconnect();
      this.client = null;
      this.player = null;
      this.status.next('disconnected');
    };
    await opening;
    this.client.onmessage = this.handleMessage.bind(this);
  }

  handleMessage(message: {data: any}): void {
    if (typeof message.data === 'string') {
      const json = JSON.parse(message.data);
      this.handleJson(json);
    }
  }

  handleJson(json: any): void {
    if (json.status !== 'ok') {
      if (environment.logMessages) {
        this.log('received', json);
      }
    }
    if (json.status) {
      this.response.next(json);
    } else {
      this.message.next(json);
    }
  }

  async login(): Promise<GenginePlayer<TRoomData, TWorldData>> {
    await this.send({type: 'login', data: this.settings.username});
    this.player = new GenginePlayer<TRoomData, TWorldData>(this.settings.username, this);
    this.status.next('login');
    return this.player;
  }

  request(request: any): void {
    if (this.client?.readyState === this.client.OPEN) {
      this.client.send(JSON.stringify(request));
    } else {
      throw new Error('client not connected');
    }
  }

  async send(request: any): Promise<any> {
    while (this.waiting) {
      await this.response.pipe(take(1)).toPromise();
    }
    this.waiting = true;
    this.request(request);
    const response = await this.response.pipe(take(1)).toPromise();
    this.waiting = false;
    if (response.status !== 'ok') {
      throw new Error(`response error ${response.status}, ${JSON.stringify(response.data)}`);
    }
    return response.data;
  }

}
